import { computed, onBeforeUnmount, onMounted, ref, type ComputedRef } from 'vue'
import type { DeckCardInput } from '../../deck/types'
import {
  coerceDeckTemplateSettings,
  resolveDeckTemplateSettings,
  type DeckTemplateSettings,
} from '../../deck/templateSettings'
import { applyDeckPlayerCheckboxState } from '../helpers/applyCheckboxState'
import { resolvePrimaryCheckbox } from '../helpers/primaryCheckbox'
import type {
  DeckPlayerCardChangeAnimation,
  DeckPlayerModel,
  DeckPlayerShortcodeProps,
} from '../types'
import { scaleShadowString } from '../../deck/shadow'

type Props = {
  deckId: string
  model: DeckPlayerModel
  cards: DeckCardInput[]
  deckTemplate: DeckTemplateSettings
  playerProps: DeckPlayerShortcodeProps
  isLoggedIn: boolean
}

type Deps = {
  props: Props
  forceDarkMode: () => boolean | undefined
  lastAdvanceOutcome: (deckId: string) => 'success' | 'failed' | null
  flowTransition: () => DeckPlayerCardChangeAnimation
  flowShowBackFirst: () => boolean
  awaitingStart: () => boolean
  activeCard: ComputedRef<DeckCardInput | null>
  activeValues: ComputedRef<boolean[]>
}

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/** Card with deck/embed/user template settings applied (user > embed > deck > card). */
function withTemplate(card: DeckCardInput, template: DeckTemplateSettings) {
  return {
    width: template.width ?? card.width,
    height: template.height ?? card.height,
    darkMode: template.darkMode ?? card.darkMode,
    backgroundColor: template.backgroundColor ?? card.backgroundColor,
    themeBackgroundColor: template.themeBackgroundColor ?? card.themeBackgroundColor,
    borderRadius: template.borderRadius ?? card.borderRadius,
    themeBorderRadius: template.themeBorderRadius ?? card.themeBorderRadius,
    borderColor: template.borderColor ?? card.borderColor,
    themeBorderColor: template.themeBorderColor ?? card.themeBorderColor,
    borderWidth: template.borderWidth ?? card.borderWidth,
    shadow: template.shadow ?? card.shadow,
    transitionDuration: template.transitionDuration ?? card.transitionDuration,
    themeTransitionDuration: template.themeTransitionDuration ?? card.themeTransitionDuration,
    flipAnimation: template.flipAnimation ?? card.flipAnimation,
    themeFlipAnimation: template.themeFlipAnimation ?? card.themeFlipAnimation,
    hoverFlip: template.hoverFlip ?? card.hoverFlip,
  }
}

/**
 * Flashcard props for a card rendered `scale` times larger. Dimensions are scaled
 * numerically (crisp) instead of with a CSS transform (blurry).
 */
function toPresentation(card: DeckCardInput, scale: number) {
  const scaled = (value: unknown) => (finite(value) ? value * scale : undefined)
  return {
    frontElements: card.frontElements,
    backElements: card.backElements,
    width: Math.max(1, Math.round((finite(card.width) ? card.width : 240) * scale)),
    height: Math.max(1, Math.round((finite(card.height) ? card.height : 160) * scale)),
    darkMode: card.darkMode,
    backgroundColor: card.backgroundColor,
    themeBackgroundColor: card.themeBackgroundColor,
    borderRadius: scaled(card.borderRadius),
    themeBorderRadius: scaled(card.themeBorderRadius),
    borderColor: card.borderColor,
    themeBorderColor: card.themeBorderColor,
    borderWidth: scaled(card.borderWidth),
    shadow: typeof card.shadow === 'string' ? scaleShadowString(card.shadow, scale) : card.shadow,
    transitionDuration: card.transitionDuration,
    themeTransitionDuration: card.themeTransitionDuration,
    flipAnimation: card.flipAnimation,
    themeFlipAnimation: card.themeFlipAnimation,
    hoverFlip: card.hoverFlip,
    showBackFirst: card.showBackFirst,
    checkboxTickStyle: card.checkboxTickStyle,
  }
}

const cardSizePx = (card: DeckCardInput, scale: number) => {
  const w = finite(card.width) ? Math.max(1, card.width) : 1
  const h = finite(card.height) ? Math.max(1, card.height) : 1
  return {
    width: `${Math.max(1, Math.round(w * scale))}px`,
    height: `${Math.max(1, Math.round(h * scale))}px`,
  }
}

/**
 * Everything about how the player looks: header switches, card-change animation,
 * the displayed card (template + checkbox state applied) and its scaling to the
 * available width, plus the pre-start preview card.
 */
export function usePlayerPresentation(deps: Deps) {
  const { props } = deps

  const ui = computed(() => ({
    // Defaults OFF (minimal = just the card).
    showDeckTitle: props.playerProps.showDeckTitle === true,
    showCount: props.playerProps.showCount === true,
    showProgressBar: props.playerProps.showProgressBar === true,
    cardChangeAnimation: ((): DeckPlayerCardChangeAnimation => {
      // In flowchart mode each pull-next-card node chooses its own transition.
      if (props.model === 'flowchart') return deps.flowTransition()
      const outcome = deps.lastAdvanceOutcome(props.deckId || 'default')
      const base = (props.playerProps.cardChangeAnimation ??
        props.deckTemplate.cardChangeAnimation ??
        'none') as DeckPlayerCardChangeAnimation
      if (outcome === 'success') {
        return (props.playerProps.successCardChangeAnimation ??
          props.deckTemplate.successCardChangeAnimation ??
          base) as DeckPlayerCardChangeAnimation
      }
      if (outcome === 'failed') {
        return (props.playerProps.failedCardChangeAnimation ??
          props.deckTemplate.failedCardChangeAnimation ??
          base) as DeckPlayerCardChangeAnimation
      }
      return base
    })(),
    showLoginHint: props.playerProps.showLoginHint === true,
  }))

  const resolvedTemplateFor = (card: DeckCardInput) =>
    resolveDeckTemplateSettings({
      user: { forceDarkMode: deps.forceDarkMode() },
      embed: coerceDeckTemplateSettings(props.playerProps as unknown),
      deck: props.deckTemplate ?? null,
      card: coerceDeckTemplateSettings(card as unknown),
    })

  const primaryRef = computed(() => {
    const card = deps.activeCard.value
    return card ? resolvePrimaryCheckbox(card.frontElements, card.backElements) : null
  })

  /** The active card as displayed: checkbox state patched in, templates resolved. */
  const patchedCard = computed(() => {
    const card = deps.activeCard.value
    if (!card) return null
    const patched = applyDeckPlayerCheckboxState({
      front: card.frontElements,
      back: card.backElements,
      primary: primaryRef.value,
      primaryValues: deps.activeValues.value,
      primaryReadOnly: props.model === 'flowchart',
    })
    const template = resolvedTemplateFor(card)
    return {
      ...card,
      frontElements: patched.front,
      backElements: patched.back,
      ...withTemplate(card, template),
      showBackFirst:
        props.model === 'flowchart'
          ? deps.flowShowBackFirst()
          : (template.showBackFirst ?? card.showBackFirst),
      checkboxTickStyle: template.checkboxTickStyle ?? card.checkboxTickStyle,
    } satisfies DeckCardInput
  })

  /** First card shown behind the pre-start screen, before the flow starts. */
  const previewCard = computed((): DeckCardInput | null => {
    if (!deps.awaitingStart()) return null
    const card = props.cards[0]
    if (!card) return null
    const template = resolvedTemplateFor(card)
    return {
      ...card,
      ...withTemplate(card, template),
      showBackFirst: false,
      checkboxTickStyle: template.checkboxTickStyle ?? card.checkboxTickStyle,
    }
  })

  // Scale
  const requestedScale = computed(() => {
    const raw = props.playerProps.scale
    return Math.max(0.01, Math.min(50, finite(raw) ? raw : 5))
  })
  const ignoreBound = computed(() => props.playerProps.ignoreBound === true)

  /**
   * Player width: card width × requested scale, kept within 240–1200 px. (The
   * `playerProps.width` prop sizes the card, not this container.)
   */
  const frameWidthPx = computed(() => {
    const card = patchedCard.value
    const cardWidth =
      (finite(card?.width) ? card.width : undefined) ??
      (finite(props.cards?.[0]?.width) ? props.cards[0].width : undefined) ??
      240
    const desired = cardWidth * requestedScale.value
    return Math.max(240, Math.min(1200, Number.isFinite(desired) ? desired : 900))
  })

  const frameEl = ref<HTMLElement | null>(null)
  const frameClientWidth = ref<number | null>(null)
  let resizeObserver: ResizeObserver | null = null
  onMounted(() => {
    if (typeof ResizeObserver === 'undefined' || !frameEl.value) return
    resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width
      if (finite(width) && width > 0) frameClientWidth.value = width
    })
    resizeObserver.observe(frameEl.value)
  })
  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
  })

  /** Requested scale, reduced so the card fits the available width (height may scroll). */
  const fitScale = (card: DeckCardInput | null) => {
    const scale = requestedScale.value
    if (ignoreBound.value) return scale
    if (!card) return 1
    const width = finite(card.width) ? Math.max(1, card.width) : 1
    const available = finite(frameClientWidth.value) ? frameClientWidth.value : frameWidthPx.value
    return Math.max(0.01, Math.min(scale, available / width))
  }

  const effectiveScale = computed(() => fitScale(patchedCard.value))
  const scaledOuterStyle = computed(() =>
    patchedCard.value ? cardSizePx(patchedCard.value, effectiveScale.value) : {},
  )
  const flashcardPresentation = computed(() =>
    patchedCard.value ? toPresentation(patchedCard.value, effectiveScale.value) : null,
  )

  const previewEffectiveScale = computed(() => fitScale(previewCard.value))
  const previewScaledOuterStyle = computed(() => {
    const card = previewCard.value
    if (!card) return {}
    const radius =
      typeof card.borderRadius === 'number'
        ? card.borderRadius
        : typeof card.themeBorderRadius === 'number'
          ? card.themeBorderRadius
          : 12
    return {
      ...cardSizePx(card, previewEffectiveScale.value),
      borderRadius: `${Math.round(radius * previewEffectiveScale.value)}px`,
    }
  })
  const previewPresentation = computed(() =>
    previewCard.value ? toPresentation(previewCard.value, previewEffectiveScale.value) : null,
  )

  const showAnyHeader = computed(
    () =>
      ui.value.showDeckTitle ||
      ui.value.showCount ||
      ui.value.showProgressBar ||
      (ui.value.showLoginHint && !props.isLoggedIn),
  )

  return {
    ui,
    primaryRef,
    patchedCard,
    frameWidthPx,
    frameEl,
    effectiveScale,
    scaledOuterStyle,
    flashcardPresentation,
    previewCard,
    previewEffectiveScale,
    previewScaledOuterStyle,
    previewPresentation,
    showAnyHeader,
  }
}
